/// First-visit welcome flow. Three pages, dismissible at any
/// step. Once dismissed (Get started OR Skip) we set a
/// localStorage flag so the modal never shows again on this
/// device. The flag is versioned (`v1`) so future content
/// changes can re-trigger the flow by bumping the suffix.

import { useState } from 'react'
import { logOnboardingCompleted } from '../analytics/AnalyticsManager'

const SEEN_KEY = 'flatout.web.welcomeSeen.v1'

export function hasSeenWelcome(): boolean {
  return localStorage.getItem(SEEN_KEY) === '1'
}

export function markWelcomeSeen(): void {
  if (localStorage.getItem(SEEN_KEY) !== '1') {
    logOnboardingCompleted()
  }
  localStorage.setItem(SEEN_KEY, '1')
}

export function WelcomeModal(props: { onDismiss: () => void }) {
  const [page, setPage] = useState(0)
  const totalPages = PAGES.length
  const current = PAGES[page]!
  const isLast = page === totalPages - 1

  const dismiss = () => {
    markWelcomeSeen()
    props.onDismiss()
  }
  const next = () => {
    if (isLast) dismiss()
    else setPage((p) => Math.min(p + 1, totalPages - 1))
  }
  const back = () => setPage((p) => Math.max(p - 1, 0))

  return (
    <div className="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="max-w-md rounded-xl bg-slate-900 p-6 text-slate-100 shadow-2xl ring-1 ring-white/10">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">
          {current.eyebrow}
        </div>
        <h2 className="text-xl font-semibold tracking-wide">{current.title}</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          {current.body}
        </p>
        {current.tips && (
          <ul className="mt-3 space-y-1.5 text-xs text-slate-400">
            {current.tips.map((t, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-1 inline-block h-1 w-1 shrink-0 rounded-full bg-emerald-400" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex items-center justify-between gap-2">
          {/* Page dots */}
          <div className="flex gap-1.5">
            {PAGES.map((_, i) => (
              <span
                key={i}
                className={
                  'h-1.5 w-1.5 rounded-full ' +
                  (i === page ? 'bg-emerald-400' : 'bg-white/20')
                }
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={dismiss}
              className="text-xs text-slate-400 hover:text-white"
            >
              Skip
            </button>
            {page > 0 && (
              <button
                onClick={back}
                className="rounded-md px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
              >
                Back
              </button>
            )}
            <button
              onClick={next}
              className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white shadow hover:bg-emerald-500"
            >
              {isLast ? 'Get started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface Page {
  eyebrow: string
  title: string
  body: string
  tips?: readonly string[]
}

const PAGES: readonly Page[] = [
  {
    eyebrow: 'Welcome',
    title: 'You’re the controller',
    body:
      'FlatoutATC drops you into an air traffic control sector. Aircraft arrive, depart, and pass through; your job is to land arrivals, send departures to their exit fix, and keep everyone separated.',
  },
  {
    eyebrow: 'How to play',
    title: 'Tap, then tell it what to do',
    body:
      'Click an aircraft to select it. The panel that appears has the controls you need.',
    tips: [
      'Heading (0–359°), altitude (in feet), speed (knots) — type a value, press Enter.',
      'When close to a runway, tap "Cleared for approach" to engage the localizer, then "Cleared to land" for the touchdown.',
      'Use Direct-to or Hold to reroute. Time can be sped up to 2× / 4× / 8× from the toolbar.',
    ],
  },
  {
    eyebrow: 'Need a hand?',
    title: 'Help is one tap away',
    body:
      'The ? button on any screen opens concept cards explaining each command. The TUTORIAL level walks you through your first landing step by step — the safest place to start.',
  },
  {
    eyebrow: 'Saving progress',
    title: 'You’re playing as a Guest',
    body:
      'Free levels are unlimited and never expire. Guest progress lives in this browser only — sign in any time to keep your scores, unlocks, and leaderboard rank across devices. Inactive guest accounts are eventually cleaned up to keep things tidy.',
    tips: [
      'Sign in with Google or email + password from the top-right button. Sign in with Apple lands soon.',
      'You can keep playing as a guest indefinitely; there\'s no time wall.',
      'When you sign in, your existing guest progress is carried over — same UID, same data.',
    ],
  },
]
